/**
 * Auth API Module
 *
 * Consolidates all authentication-related API calls.
 * Provides typed interfaces for auth endpoints.
 */

import { apiClient, ApiError } from "./baseClient";
import type { UserRole } from "@/shared/types";

// ============================================
// Types
// ============================================

/**
 * Response from /auth/me endpoint
 */
export interface MeResponse {
  uid: string;
  email: string;
  display_name?: string;
  role?: string;
}

/**
 * Request body for registration
 */
export interface RegisterRequest {
  email: string;
  display_name?: string;
}

/**
 * Response from /auth/register endpoint
 */
export interface RegisterResponse {
  uid: string;
  email: string;
  role: string;
}

/**
 * Request body for email verification
 */
export interface VerifyEmailRequest {
  oob_code: string;
}

/**
 * Response from /auth/verify-email endpoint
 */
export interface VerifyEmailResponse {
  success: boolean;
  uid: string;
  email: string;
}

/**
 * Request body for password reset
 */
export interface PasswordResetRequest {
  email: string;
}

/**
 * Response from /auth/password-reset/send endpoint
 */
export interface PasswordResetResponse {
  ok: boolean;
  message?: string;
  link?: string; // Only present in dev mode
}

// ============================================
// Auth API Functions
// ============================================

/**
 * Transformed user profile for frontend use
 */
export interface UserProfileResponse {
  uid: string;
  email: string;
  displayName?: string;
  role?: UserRole;
}

/**
 * Fetches current user profile
 *
 * Requires authentication token to be set on apiClient.
 *
 * @returns User profile data
 * @throws ApiError if unauthorized or request fails
 *
 * @example
 * ```ts
 * apiClient.setAuthToken(token);
 * const profile = await fetchMe();
 * console.log(profile.email);
 * ```
 */
export async function fetchMe(): Promise<UserProfileResponse> {
  const response = await apiClient.get<MeResponse>("/auth/me");

  return {
    uid: response.uid,
    email: response.email,
    displayName: response.display_name,
    role: response.role as UserRole | undefined,
  };
}

/**
 * Registers a new user with the backend
 *
 * Called after Firebase auth registration to sync user data.
 *
 * @param data - Registration data
 * @returns Registration confirmation
 * @throws ApiError if registration fails
 *
 * @example
 * ```ts
 * apiClient.setAuthToken(firebaseToken);
 * const result = await registerUser({
 *   email: "user@example.com",
 *   display_name: "John Doe"
 * });
 * ```
 */
export async function registerUser(
  data: RegisterRequest,
): Promise<RegisterResponse> {
  return apiClient.post<RegisterResponse>("/auth/register", data);
}

/**
 * Verifies user email with OOB code
 *
 * @param oobCode - Out-of-band verification code from email link
 * @returns Verification result
 * @throws ApiError if verification fails
 *
 * @example
 * ```ts
 * apiClient.setAuthToken(firebaseToken);
 * const result = await verifyEmail({ oob_code: codeFromUrl });
 * if (result.success) {
 *   // Email verified successfully
 * }
 * ```
 */
export async function verifyEmail(
  data: VerifyEmailRequest,
): Promise<VerifyEmailResponse> {
  return apiClient.post<VerifyEmailResponse>("/auth/verify-email", data);
}

/**
 * Sends password reset email via backend API
 *
 * This endpoint:
 * - Uses the backend to generate the password reset link
 * - In development mode, returns the link directly
 * - In production, sends email via Resend
 *
 * @param email - Email address to send reset link to
 * @returns Response with optional link in dev mode
 *
 * @example
 * ```ts
 * const result = await sendPasswordReset("user@example.com");
 * if (result.link) {
 *   console.log("Dev mode link:", result.link);
 * }
 * ```
 */
export async function sendPasswordReset(
  email: string,
): Promise<PasswordResetResponse> {
  return apiClient.post<PasswordResetResponse>("/auth/password-reset/send", {
    email,
  });
}

/**
 * Checks if an API error is an authentication error
 *
 * @param error - Error to check
 * @returns true if error is auth-related
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return (
      error.httpStatus === 401 ||
      error.httpStatus === 403 ||
      error.code === "UNAUTHORIZED" ||
      error.code === "FORBIDDEN" ||
      error.code === "TOKEN_EXPIRED"
    );
  }
  return false;
}

/**
 * Checks if an API error is a not found error
 *
 * @param error - Error to check
 * @returns true if error is 404
 */
export function isNotFoundError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.httpStatus === 404 || error.code === "NOT_FOUND";
  }
  return false;
}

/**
 * Extracts user-friendly error message from API error
 *
 * @param error - Error to extract message from
 * @param fallback - Fallback message if extraction fails
 * @returns User-friendly error message
 */
export function getErrorMessage(
  error: unknown,
  fallback = "An error occurred",
): string {
  if (error instanceof ApiError) {
    return error.message || error.code || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

// ============================================
// Re-exports for convenience
// ============================================

export { apiClient, ApiError } from "./baseClient";
export type { RequestConfig } from "./baseClient";
